"use client";
import dayjs from "@dayjs";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { SocialLinks } from "@/app/api/utils/types/tenants";
import type { LocalizedText } from "@/app/api/utils/types/tenants";
import { useTenantBranding } from "@/app/context/TenantBrandingContext";

function getLocalizedText(text: LocalizedText | undefined, locale: string): string {
  if (!text) return "";
  return text[locale as keyof LocalizedText] || text.en || "";
}

const getFooterLinks = (t: ReturnType<typeof useTranslations>) => [
  {
    title: t("Footer.support"),
    links: [{ name: t("Footer.contact"), url: "/contact" }],
  },
  {
    title: t("Footer.legal"),
    links: [
      { name: t("Footer.privacyPolicy"), url: "/privacy-policy" },
      { name: t("Footer.termsAndConditions"), url: "/conditions" },
    ],
  },
];

const DEFAULT_SOCIAL_ICONS = [
  {
    key: "twitter" as keyof SocialLinks,
    src: "/assets/v2/icons/footer/twitter.svg",
    alt: "Twitter",
    defaultUrl: "https://x.com/gate",
  },
  {
    key: "facebook" as keyof SocialLinks,
    src: "/assets/v2/icons/footer/facebook.svg",
    alt: "Facebook",
    defaultUrl: "https://www.facebook.com/gatecomglobal",
  },
  {
    key: "telegram" as keyof SocialLinks,
    src: "/assets/v2/icons/footer/telegram.svg",
    alt: "Telegram",
    defaultUrl: "https://t.me/GateCom_EN",
  },
  {
    key: "instagram" as keyof SocialLinks,
    src: "/assets/v2/icons/footer/instagram.svg",
    alt: "Instagram",
    defaultUrl: "https://www.instagram.com/gate.com_global/",
  },
  {
    key: "linkedin" as keyof SocialLinks,
    src: "/assets/v2/icons/footer/linkedin.svg",
    alt: "LinkedIn",
    defaultUrl: "https://www.linkedin.com/company/gateio/",
  },
  {
    key: "discord" as keyof SocialLinks,
    src: "/assets/v2/icons/footer/discord.svg",
    alt: "Discord",
    defaultUrl: "https://discord.gg/QZWFkuQvzP",
  },
  {
    key: "youtube" as keyof SocialLinks,
    src: "/assets/v2/icons/footer/youtube.svg",
    alt: "YouTube",
    defaultUrl: "https://youtube.com/@gateexchange",
  },
];

function Footer() {
  const { name: tenantName, logoUrl, socialLinks, footer, copyright } = useTenantBranding();
  const locale = useLocale();
  const [platform, setPlatform] = useState<"android" | "ios" | "unknown">(
    "unknown"
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.gateio && typeof window.gateio.gatepay === "function") {
        setPlatform("android");
      } else if (window.webkit?.messageHandlers?.gatepay) {
        setPlatform("ios");
      }
    }
  }, []);
  const t = useTranslations();

  return (
    <div className="bg-tenant-secondary">
      <div className="container mx-auto px-0">
        <div className="sm:px-0 py-10 px-4 lg:grid grid-cols-3 gap-10 overflow-hidden">
          <div className="col-span-2">
            <div className="h-10 relative">
              {logoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={logoUrl} className="h-10 w-auto max-w-[240px] object-contain" alt={tenantName} />
              ) : (
                <span className="text-white font-switzer font-bold text-xl leading-10">{tenantName}</span>
              )}
            </div>

            <div className="text-white text-base font-normal font-switzer leading-tight mt-6">
              {getLocalizedText(footer?.text, locale) || t("Footer.about", { tenantName })}
            </div>
          </div>

          <div className="flex flex-col justify-between lg:mt-0 mt-6 lg:gap-5">
            <div className="flex lg:flex-row flex-col gap-6 lg:items-start">
              {getFooterLinks(t).map((section) => (
                <div
                  key={section.title}
                  className="flex flex-col justify-start items-start gap-1 sm:min-w-32"
                >
                  <div className="text-white text-base font-bold font-switzer leading-normal ">
                    {section.title}
                  </div>
                  <div className="flex lg:flex-col flex-row gap-x-3">
                    {section.links.map((link) => (
                      <a
                        key={link.name}
                        className="opacity-50 text-white text-base font-normal font-switzer leading-normal hover:text-primary"
                        href={link.url}
                        target="_self"
                        rel="noopener noreferrer"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="inline-flex justify-between items-center gap-x-4 md:gap-x-2 lg:mt-0 mt-6">
              {DEFAULT_SOCIAL_ICONS.map((icon) => {
                const url = socialLinks?.[icon.key] || icon.defaultUrl;
                const shouldHide =
                  platform === "android" &&
                  (url.includes("facebook") || url.includes("t.me"));

                if (shouldHide) return null;

                return (
                  <a
                    key={icon.key}
                    href={url}
                    rel="noopener noreferrer"
                    className="w-6 h-6 relative overflow-hidden"
                  >
                    <Image
                      src={icon.src}
                      alt={icon.alt}
                      width={24}
                      height={24}
                      className="w-6 h-6 absolute inset-0 m-auto"
                    />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 bg-tenant-secondary flex flex-col justify-center items-center">
          <div className="opacity-50 text-center text-white text-base font-normal font-switzer leading-normal">
            {copyright || (
              <>
                {t("Footer.copyright", { year: dayjs().year() })}
                <br className="sm:hidden" /> {t("Footer.allRightReserved")}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Footer;
